import * as L from 'leaflet'
import { createFeatureLayer } from '../features'
import MapUIConfig, { GeoJsonProperties, MapCreationOptions, OverrideConfig } from '../types/config'
import { FeatureCollectionWithIds } from '../types/geojson'
import { MAP_DEFAULTS } from './projection'

export const MAP_DEFAULT_OPTIONS: MapCreationOptions = {
  center: MAP_DEFAULTS.DEFAULT_CENTER,
  crs: MAP_DEFAULTS.CRS,
  zoom: MAP_DEFAULTS.DEFAULT_ZOOM,
  minZoom: MAP_DEFAULTS.MIN_ZOOM,
  maxZoom: MAP_DEFAULTS.MAX_ZOOM,
}

export const DEFAULT_ZOOM_CONTROL_POSITION = 'topright'

export function createMapAndBaseLayers(
  mapElement: HTMLElement,
  { baseLayers, features, zoomControlPosition, ...mapOptions }: MapCreationOptions,
): L.Map {
  const map = L.map(mapElement, {
    ...MAP_DEFAULT_OPTIONS,
    ...mapOptions,
  })

  setupMap(map, { baseLayers, features, zoomControlPosition })

  return map
}

export function setupMap(
  map: L.Map,
  {
    zoomControlPosition,
    baseLayers,
    features,
  }: Pick<MapCreationOptions, 'zoomControlPosition' | 'baseLayers' | 'features'>,
) {
  const position = zoomControlPosition ?? DEFAULT_ZOOM_CONTROL_POSITION

  if (position) map.zoomControl?.setPosition(position)

  if (baseLayers) baseLayers.addTo(map)

  if (features) map.addLayer(features)

  return map
}

export interface MapViewOptions {
  zoomLevel?: number
  bounds?: L.LatLngBoundsExpression
}

export function setMapViewWithMarker(map: L.Map, marker: L.Marker<any>, position: L.LatLng, options?: MapViewOptions) {
  setMapView(map, position, options)
  // Add marker at the position
  marker.addTo(map)
}

export function setMapView(map: L.Map, position: L.LatLng, options?: MapViewOptions): void {
  map.setView(position, options?.zoomLevel)

  if (options?.bounds) {
    map.fitBounds(options.bounds)
  }
}

export function constructBasemapLayers<
  TProperties extends GeoJsonProperties = GeoJsonProperties,
  TRenderContext = unknown,
>(config: MapUIConfig<TProperties, TRenderContext>) {
  return new L.LayerGroup(
    config.basemapLayers.map((l) =>
      L.tileLayer(l.url, {
        attribution: l.attribution,
        maxZoom: l.maxZoom,
        minZoom: l.minZoom,
        ...(l.layerOptions ?? {}),
      }),
    ),
  )
}

/*
Gets the size of a Polyline as it appears on a map.
*/
function getPolylineSize(map: L.Map, layer: L.Polyline) {
  const topLeft = map.latLngToContainerPoint(layer.getBounds().getNorthWest())
  const bottomRight = map.latLngToContainerPoint(layer.getBounds().getSouthEast())
  const width = bottomRight.x - topLeft.x
  const height = bottomRight.y - topLeft.y
  return { width, height }
}

/*
Hide a Polyline's tooltip if it's bounds exceed the bounds of the Polyline.
*/
export function hideOverflowingPolylineTooltip(map: L.Map, layer: L.Polyline) {
  const node: HTMLElement | undefined = layer.getTooltip()?.getElement()
  if (!node) return
  const layerSize = getPolylineSize(map, layer)
  const tooltipBoundary = node.getBoundingClientRect()

  // Hide the object with opacity if its size is larger than the size of the layer
  node.style.opacity = layerSize.width < tooltipBoundary.width || layerSize.height < tooltipBoundary.height ? '0' : '1'
}

export function configureMapForDisplay<
  TProperties extends GeoJsonProperties = GeoJsonProperties,
  TRenderContext = unknown,
>(
  mapElement: HTMLElement,
  config: MapUIConfig<TProperties, TRenderContext>,
  mapCreationFunction: typeof createMapAndBaseLayers,
  { features, overrides }: { features?: FeatureCollectionWithIds<TProperties>; overrides?: OverrideConfig },
) {
  let featureLayer = features ? createFeatureLayer(config, features, overrides?.classMappings) : null

  return mapCreationFunction(mapElement, {
    crs: config.crs,
    zoom: config.defaultZoom,
    minZoom: config.basemapLayers[0].minZoom,
    maxZoom: config.basemapLayers[0].maxZoom,
    center: featureLayer ? featureLayer.getBounds().getCenter() : overrides?.center ?? MAP_DEFAULTS.DEFAULT_CENTER,
    baseLayers: constructBasemapLayers(config),
  })
}
